import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { handlePageClick, handlePageSizeChange } from "helpers/PaginationHelper";
import { CustomTable, OptionType, Pagination, TableColumn, TableType } from "modules/common";
import { useMemo } from "react";
import { SortingRule } from "react-table";
import { OuterModule, OuterModuleResponse } from "services";

export interface IOuterModulesTable {
    outerModules?: OuterModuleResponse;
    isLoading: boolean;
    setSortBy?: (sortBy: SortingRule<object>) => void;
    currentPage?: number;
    setCurrentPage?: React.Dispatch<React.SetStateAction<number>>;
    pageSize?: OptionType<string>;
    setPageSize?: React.Dispatch<React.SetStateAction<OptionType<string>>>;
    onRowSelect?: (row: OuterModule) => void;
}

const MAX_URL_LENGTH = 50;

const DEFAULT_TABLE_COLUMNS: TableColumn<OuterModule>[] = [
    {
        Header: "REDNI BROJ",
        accessor: "orderNumber",
        width: 2,
    },
    {
        Header: "NAZIV",
        accessor: "name",
        width: 4,
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span style={{ fontWeight: "bold" }}>{original.name}</span>;
        },
    },
    {
        Header: "STATUS",
        accessor: "isActive",
        width: 2,
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span>{original.isActive ? "Aktivan" : "Neaktivan"}</span>;
        },
    },
    {
        Header: "JAVNO DOSTUPAN",
        accessor: "isPublic",
        width: 2,
        Cell: ({ row }: any) => {
            const { original } = row;
            return <span>{original.isPublic ? "DA" : "NE"}</span>;
        },
    },
    {
        Header: "URL",
        accessor: "url",
        width: 4,
        Cell: ({ row }: any) => {
            const { original } = row;
            const elipsis = original.url.length > MAX_URL_LENGTH ? "..." : "";
            return (
                <a className="email-link-cell" href={original.url}>
                    {original.url.slice(0, MAX_URL_LENGTH) + elipsis}
                </a>
            );
        },
    },
];

export const OuterModulesTable = ({
    outerModules,
    isLoading,
    setSortBy,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    onRowSelect,
}: IOuterModulesTable) => {
    const tableColumns = useMemo(() => {
        let columns = DEFAULT_TABLE_COLUMNS;
        if (!setSortBy) columns = columns.map(column => ({ ...column, disableSortBy: true }));
        if (onRowSelect) {
            const infoColumn = {
                id: "info",
                disableSortBy: true,
                width: 1,
                Cell: ({ row }: any) => {
                    return (
                        <DetailsIcon
                            className="details-icon"
                            onClick={onRowSelect && (() => onRowSelect(row.original))}
                        />
                    );
                },
            };

            columns = [...columns, infoColumn];
        }

        return columns;
    }, [DEFAULT_TABLE_COLUMNS]);

    return (
        <div className="outer-modules-table">
            <CustomTable
                flex
                data={outerModules?.items ?? []}
                columns={tableColumns}
                loading={isLoading}
                setSortBy={setSortBy}
                tableType={TableType.OuterModuleTable}
            />

            {currentPage && outerModules && outerModules.items.length > 0 && (
                <Pagination
                    currentPage={currentPage ?? 1}
                    numberOfPages={Math.ceil(outerModules.total / parseInt(pageSize!.value))}
                    pageNeighbours={1}
                    pageSize={pageSize!}
                    onPageSizeChange={v => handlePageSizeChange(v, setPageSize!, setCurrentPage!)}
                    handlePageClick={e => handlePageClick(e, setCurrentPage!)}
                    handlePageSelect={e => setCurrentPage!(e)}
                    next={() => setCurrentPage!(p => p + 1)}
                    prev={() => setCurrentPage!(p => p - 1)}
                    first={() => setCurrentPage!(1)}
                    last={() =>
                        setCurrentPage!(Math.ceil(outerModules.total / parseInt(pageSize!.value)))
                    }
                />
            )}
        </div>
    );
};
